import { lazy, Suspense, useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, ChevronDown, Scale, ShieldCheck, Briefcase, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProjectIssuesTracker from '@/components/postoc/ProjectIssuesTracker';
import ProjectSectionDocs from '@/components/ProjectSectionDocs';
import PossessionPicture from '@/components/living/PossessionPicture';
import AffectsMeCard from '@/components/group/AffectsMeCard';
import { CorrespondenceRepository, NoticesAgenda, MeetingsMinutes, IssueThreads } from '@/components/UsSection';

const CorpusWarRoom = lazy(() => import('@/components/CorpusWarRoom'));
const API = process.env.REACT_APP_BACKEND_URL;

const Block = ({ id, title, sub, open = false, children }) => (
  <details open={open} className="group rounded-xl border bg-card" data-testid={`room-block-${id}`}>
    <summary className="flex items-center gap-2 cursor-pointer list-none p-4 select-none">
      <div className="flex-1">
        <h2 className="font-semibold text-sm">{title}</h2>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
    </summary>
    <div className="px-4 pb-4">{children}</div>
  </details>
);

function MinutesList({ projectId, topicKey }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/topics/${projectId}/${topicKey}/minutes`).then((r) => setDocs(r.data.minutes)).catch(() => setDocs([]));
  }, [projectId, topicKey]);
  if (!docs?.length) return null;
  return (
    <div className="mb-3 space-y-1" data-testid="room-minutes">
      <p className="text-xs font-medium text-muted-foreground">Meeting minutes that discussed this topic</p>
      {docs.map((d) => (
        <p key={d.id} className="text-sm flex items-center gap-2"><FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{d.title}{d.doc_date ? ` · ${d.doc_date}` : ''}</p>
      ))}
    </div>
  );
}

const Escalation = ({ onNavigate }) => (
  <div className="grid gap-2 sm:grid-cols-3" data-testid="room-escalation">
    {[['legal-services', Scale, 'S.18 interest & refund'], ['post-possession-rights', ShieldCheck, 'Post-possession rights'], ['service-providers', Briefcase, 'Lawyers & service providers']].map(([s, Icon, l]) => (
      <Button key={s} variant="outline" className="justify-start" onClick={() => onNavigate(s)}><Icon className="h-4 w-4 mr-2" />{l}</Button>
    ))}
  </div>
);

// One Buyers' Group topic room, same layout for every topic (Corpus keeps its full existing room).
export default function TopicRoom({ topic, projectId, onNavigate, onOpenAuth, isVerified, isAdmin }) {
  const Icon = topic.icon;
  useEffect(() => { window.scrollTo(0, 0); }, [topic.key]);
  const header = (
    <div className="flex items-start gap-3">
      <div className="p-2.5 rounded-lg shrink-0 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"><Icon className="h-6 w-6" /></div>
      <div>
        <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="topic-room-title">{topic.label}</h1>
        <p className="text-sm text-muted-foreground">{topic.hint}</p>
      </div>
    </div>
  );
  if (!isVerified) {
    return (
      <div className="space-y-4 p-4 sm:p-6" data-testid={`topic-room-${topic.key}`}>
        {header}
        <div className="rounded-xl border p-5 text-center space-y-3" data-testid="topic-room-locked">
          <Lock className="h-6 w-6 mx-auto text-muted-foreground" />
          <p className="text-sm">This room is for verified flat owners of the project.</p>
          <Button onClick={() => onNavigate('verify-ownership')}>Verify ownership</Button>
        </div>
      </div>
    );
  }
  // Wing-level concerns cut across topics, so that thread shows in every room's discussion.
  const discussion = (
    <Block id="discussion" title="Discussion" sub="Members' experiences and evidence, moderated">
      <IssueThreads projectId={projectId} isAdmin={isAdmin} subcategories={[...topic.threads, 'wing_level_concerns']} />
    </Block>
  );
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid={`topic-room-${topic.key}`}>
      {header}
      {topic.group === 'pre' && <AffectsMeCard projectId={projectId} topicKey={topic.key} isAdmin={isAdmin} />}
      {topic.key === 'corpus' ? (
        <>
          <Suspense fallback={<Loader2 className="h-5 w-5 animate-spin mx-auto my-6" />}>
            <CorpusWarRoom projectId={projectId} onNavigate={onNavigate} />
          </Suspense>
          {discussion}
        </>
      ) : (
        <>
          <Block id="status" title="Status & open commitments" sub="What the developer has committed, and by when" open>
            {topic.pp ? <ProjectIssuesTracker projectId={projectId} onOpenAuth={onOpenAuth} lockCategory={topic.pp} />
              : <p className="text-sm text-muted-foreground">No commitments tracked for this topic yet. They appear here when meeting minutes record them.</p>}
            {topic.key === 'construction_delays' && <div className="mt-4"><PossessionPicture projectId={projectId} onOpenAuth={onOpenAuth} /></div>}
          </Block>
          <Block id="documents" title="Documents" sub="Just add the file; AI names and summarises it" open>
            {topic.pp && <MinutesList projectId={projectId} topicKey={topic.key} />}
            <ProjectSectionDocs projectId={projectId} defaultCategory={topic.docCat} allowedCategories={[topic.docCat]}
              title={`${topic.label}: documents`} emptyText="No documents yet. Members can add letters, bills, photos or notices here." />
          </Block>
          <Block id="correspondence" title="Correspondence" sub="Letters and emails with the developer on this topic">
            <CorrespondenceRepository projectId={projectId} isAdmin={isAdmin} topic={topic.key} />
          </Block>
          <Block id="meetings" title="Meetings & notices" sub="Notices, agendas and minutes on this topic">
            <div className="space-y-6">
              <NoticesAgenda projectId={projectId} isAdmin={isAdmin} topic={topic.key} />
              <MeetingsMinutes projectId={projectId} isAdmin={isAdmin} topic={topic.key} />
            </div>
          </Block>
          {discussion}
        </>
      )}
      <Block id="escalation" title="Escalation & legal" sub="When talks do not resolve it">
        <Escalation onNavigate={onNavigate} />
        <div className="mt-4"><IssueThreads projectId={projectId} isAdmin={isAdmin} subcategories={['legal_action']} /></div>
      </Block>
    </div>
  );
}
